'use client';

import { Button, Modal, LinkStyled } from '@components/common';
import {
  XMarkIcon, PencilSquareIcon, VideoCameraIcon, PhoneIcon,
} from '@heroicons/react/20/solid';
import { useTranslation } from 'next-i18next';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';

interface InterviewSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * @description Modal selector for interview options (Video / Audio / Text)
 */
export const InterviewSelector = ({ isOpen, onClose }: InterviewSelectorProps) => {
  const { t } = useTranslation('common');
  const { isAuth, redirectToLogin } = useAuthClient();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" className="!p-0">
      <div className="relative px-4 py-10 text-center text-ink">
        {/* Close button */}
        <Button
          onClick={onClose}
          styling="solid"
          rounding="full"
          color="ink"
          className="absolute right-2 top-2 flex aspect-square items-center justify-center !p-1"
          aria-label="Close"
        >
          <XMarkIcon className="size-4" />
        </Button>

        <h3 className="mb-6 text-lg font-bold text-ink">
          {t('ai.conversation.start')}
        </h3>

        {/* Action buttons */}
        <div className="space-y-3">
          {/* Video Conversation Button */}
          <LinkStyled
            href="/ai-interview?autostart=1"
            styling="solid"
            rounding="full"
            color="secondary"
            className="flex w-full items-center justify-center gap-3"
            onClick={(e) => {
              if (!isAuth) {
                e.preventDefault();
                redirectToLogin('/ai-interview?autostart=1');
              }
            }}
          >
            <VideoCameraIcon className="size-5" />
            <span className="font-medium">{t('ai.conversation.video')}</span>
          </LinkStyled>

          {/* Audio Conversation Button */}
          <LinkStyled
            href="/audio-interview"
            styling="solid"
            rounding="full"
            color="primary"
            className="flex w-full items-center justify-center gap-3"
            onClick={(e) => {
              if (!isAuth) {
                e.preventDefault();
                redirectToLogin('/audio-interview');
              }
            }}
          >
            <PhoneIcon className="size-5" />
            <span className="font-medium">{t('ai.conversation.audio')}</span>
          </LinkStyled>

          {/* Text Conversation Button */}
          <LinkStyled
            href="/bestie"
            styling="solid"
            rounding="full"
            color="ink"
            className="flex w-full items-center justify-center gap-3"
          >
            <PencilSquareIcon className="size-5" />
            <span className="font-medium">{t('ai.conversation.text')}</span>
          </LinkStyled>
        </div>
      </div>
    </Modal>
  );
};

export default InterviewSelector;
